import React from 'react';
import Card from 'react-bootstrap/Card';
import { createEmptyRecord, RESPONSE_VALUES, LATERALITY_OPTIONS } from './ConsolidatedFamilyHistoryTab';

const normalizeValue = (value) => {
    if (value === RESPONSE_VALUES.NA) return 'N/A';
    if (value === RESPONSE_VALUES.UNKNOWN) return 'Unknown';
    return value;
};

const isChecked = (uiValue, storedValue) => {
    if (uiValue === RESPONSE_VALUES.UNKNOWN && storedValue === 'Unknown') return true;
    if (uiValue === RESPONSE_VALUES.NA && storedValue === 'N/A') return true;
    return storedValue === uiValue;
};

const BreastProblemSection = ({ historyItem, label, problemData, onFieldChange, disabled, showFocal = false }) => {
    const handleChange = (field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleValueChange = (value) => {
        const normalized = normalizeValue(value);
        if (normalized === RESPONSE_VALUES.NO || normalized === 'Unknown') {
            onFieldChange(historyItem, {
                value: normalized,
                laterality: '',
                focalPainYN: '',
                notes: ''
            });
        } else {
            onFieldChange(historyItem, { value: normalized });
        }
    };

    return (
        <div className="mb-3 ml-4">
            <label className="form-label">{label}</label>
            <div className="mb-2">
                {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                    <div key={value} className="custom-control custom-radio custom-control-inline">
                        <input
                            type="radio"
                            id={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                            name={historyItem}
                            className="custom-control-input"
                            value={value}
                            onChange={(e) => handleValueChange(e.target.value)}
                            checked={isChecked(value, problemData?.value)}
                            disabled={disabled}
                        />
                        <label className="custom-control-label" htmlFor={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                            {value}
                        </label>
                    </div>
                ))}
            </div>

            {problemData?.value === RESPONSE_VALUES.YES && (
                <div className="row">
                    <div className="form-group mb-2 col-12 col-lg-2">
                        <label className="form-label">Laterality</label>
                        <select
                            className="form-control-custom"
                            value={problemData?.laterality || ''}
                            onChange={(e) => handleChange('laterality', e.target.value)}
                            disabled={disabled}
                        >
                            {LATERALITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {showFocal && (
                        <div className="form-group mb-2 col-12 col-lg-1">
                            <label className="form-label">Focal</label>
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id={`${historyItem}-focal`}
                                    className="custom-control-input"
                                    checked={problemData?.focalPainYN === '1' || problemData?.focalPainYN === 1}
                                    onChange={(e) => handleChange('focalPainYN', e.target.checked ? '1' : '0')}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`${historyItem}-focal`}></label>
                            </div>
                        </div>
                    )}

                    <div className={`form-group col-12 ${showFocal ? 'col-lg-9' : 'col-lg-10'}`}>
                        <label className="form-label">Notes</label>
                        <input
                            type="text"
                            className="form-control-custom"
                            value={problemData?.notes || ''}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const BreastMarkingSection = ({ historyItem, label, markingData, onFieldChange, disabled, showIsNew = false }) => {
    const handleChange = (field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleValueChange = (value) => {
        const normalized = normalizeValue(value);
        if (normalized === RESPONSE_VALUES.NO || normalized === 'Unknown') {
            onFieldChange(historyItem, {
                value: normalized,
                laterality: '',
                newYN: '',
                notes: ''
            });
        } else {
            onFieldChange(historyItem, { value: normalized });
        }
    };

    return (
        <div className="mb-3 ml-4">
            <label className="form-label">{label}</label>
            <div className="mb-2">
                {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                    <div key={value} className="custom-control custom-radio custom-control-inline">
                        <input
                            type="radio"
                            id={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                            name={historyItem}
                            className="custom-control-input"
                            value={value}
                            onChange={(e) => handleValueChange(e.target.value)}
                            checked={isChecked(value, markingData?.value)}
                            disabled={disabled}
                        />
                        <label className="custom-control-label" htmlFor={`${historyItem}-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                            {value}
                        </label>
                    </div>
                ))}
            </div>

            {markingData?.value === RESPONSE_VALUES.YES && (
                <div className="row">
                    <div className="form-group mb-2 col-12 col-lg-2">
                        <label className="form-label">Laterality</label>
                        <select
                            className="form-control-custom"
                            value={markingData?.laterality || ''}
                            onChange={(e) => handleChange('laterality', e.target.value)}
                            disabled={disabled}
                        >
                            {LATERALITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {showIsNew && (
                        <div className="form-group mb-2 col-12 col-lg-2">
                            <label className="form-label">Is it new?</label>
                            <select
                                className="form-control-custom"
                                value={markingData?.newYN || ''}
                                onChange={(e) => handleChange('newYN', e.target.value)}
                                disabled={disabled}
                            >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        </div>
                    )}

                    <div className={`form-group col-12 ${showIsNew ? 'col-lg-8' : 'col-lg-10'}`}>
                        <label className="form-label">Notes</label>
                        <input
                            type="text"
                            className="form-control-custom"
                            value={markingData?.notes || ''}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const PatientBreastCurrentCard = ({ medicalHistory, onFieldChange, onMultipleFieldChanges, disabled = false }) => {
    const handleBreastProblemsChange = (value) => {
        const normalized = normalizeValue(value);
        onFieldChange('PatientHasBreastProblems', { value: normalized });
        
        if (normalized === RESPONSE_VALUES.NO || normalized === 'N/A') {
            onMultipleFieldChanges({
                PatientHasBreastProblems: { ...createEmptyRecord('PatientHasBreastProblems'), value: normalized },
                BreastPain: createEmptyRecord('BreastPain'),
                NippleDischarge: createEmptyRecord('NippleDischarge'),
                LumpsThickness: createEmptyRecord('LumpsThickness'),
                NippleRetraction: createEmptyRecord('NippleRetraction'),
                OtherBreastProblems: createEmptyRecord('OtherBreastProblems')
            });
        }
    };

    const handleBreastMarkingsChange = (value) => {
        const normalized = normalizeValue(value);
        onFieldChange('PatientHasBreastMarkings', { value: normalized });
        
        if (normalized === RESPONSE_VALUES.NO || normalized === 'N/A') {
            onMultipleFieldChanges({
                PatientHasBreastMarkings: { ...createEmptyRecord('PatientHasBreastMarkings'), value: normalized },
                Moles: createEmptyRecord('Moles'),
                Scars: createEmptyRecord('Scars'),
                Bruises: createEmptyRecord('Bruises'),
                OtherBreastMarkings: createEmptyRecord('OtherBreastMarkings')
            });
        }
    };

    const showBreastProblems = medicalHistory.PatientHasBreastProblems?.value === RESPONSE_VALUES.YES;
    const showBreastMarkings = medicalHistory.PatientHasBreastMarkings?.value === RESPONSE_VALUES.YES;

    return (
        <Card className="pinkCard mb-3">
            <Card.Header>
                <span>PATIENT BREAST CURRENT</span>
            </Card.Header>
            <Card.Body>
                <h4><strong><em>Answer the following</em></strong></h4>

                {/* Breast Problems Question */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Is the patient having Breast problems?
                        <span className="color-pink"> *</span>
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.NA].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`PatientHasBreastProblems-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="PatientHasBreastProblems"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleBreastProblemsChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.PatientHasBreastProblems?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`PatientHasBreastProblems-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Breast Problems Details */}
                {showBreastProblems && (
                    <div className="mb-4">
                        <BreastProblemSection
                            historyItem="BreastPain"
                            label="Breast Pain"
                            problemData={medicalHistory.BreastPain}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                            showFocal={true}
                        />
                        <BreastProblemSection
                            historyItem="NippleDischarge"
                            label="Nipple Discharge"
                            problemData={medicalHistory.NippleDischarge}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <BreastProblemSection
                            historyItem="LumpsThickness"
                            label="Lumps/Thickness"
                            problemData={medicalHistory.LumpsThickness}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <BreastProblemSection
                            historyItem="NippleRetraction"
                            label="Nipple Retraction"
                            problemData={medicalHistory.NippleRetraction}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <BreastProblemSection
                            historyItem="OtherBreastProblems"
                            label="Other Problems"
                            problemData={medicalHistory.OtherBreastProblems}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                    </div>
                )}

                {/* Breast Markings Question */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Does the patient have Breast Markings?
                        <span className="color-pink"> *</span>
                    </label>

                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES, RESPONSE_VALUES.NA].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`PatientHasBreastMarkings-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="PatientHasBreastMarkings"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleBreastMarkingsChange(e.target.value)}
                                    checked={isChecked(value, medicalHistory.PatientHasBreastMarkings?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`PatientHasBreastMarkings-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Breast Markings Details */}
                {showBreastMarkings && (
                    <div className="mb-4">
                        <BreastMarkingSection
                            historyItem="Moles"
                            label="Moles"
                            markingData={medicalHistory.Moles}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                            showIsNew={true}
                        />
                        <BreastMarkingSection
                            historyItem="Scars"
                            label="Scars"
                            markingData={medicalHistory.Scars}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <BreastMarkingSection
                            historyItem="Bruises"
                            label="Bruises"
                            markingData={medicalHistory.Bruises}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                        <BreastMarkingSection
                            historyItem="OtherBreastMarkings"
                            label="Other Markings"
                            markingData={medicalHistory.OtherBreastMarkings}
                            onFieldChange={onFieldChange}
                            disabled={disabled}
                        />
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default PatientBreastCurrentCard;
